import React, { useEffect, useState } from "react";
import moment from "moment";
import { cls } from "@iscr/views/NUI/utils";
import { defer } from "@iscr/xAppLib/helpers/defer";
import { Button, Card, CardGroup, Icon, Image, Modal } from "semantic-ui-react";
import { useAsyncUpdate } from "xAppLib/Hooks/useAsync";
import API_service from "xAppLib/providers/API_service";
import SimpleShowMore from "xAppLib/UIelems/SimpleShowMore";
import SIcon from "views/NUI/StreamlineIcons/SIcon";
import { fetchAsBlobUrl } from "../../../../helpers/api_file";

/** @typedef {Object} UploadedFile
 * @property {string} res
 * @property {string} bct
 * @property {string} url
 * @property {string} img_fn
 * @property {string} tm
 * @property {string} comn
 * @property {boolean} is_attachment
 * @property {string} image_source
 */

/**
 *
 * @param {Object[]} files
 * @param {(remainingFiles: UploadedFile[], removedFiles: UploadedFile[]) => Promise<void> | void} onChange
 * @param {boolean=false} readOnly
 * @param {number=} itemsPerRow
 * @param {((file: UploadedFile)=>void)=} onFileClickHandler
 * @param {boolean=false} basic
 * @param {boolean} confirmAsOverlay
 * @param {React.JSX.Element=} extra
 * @return {React.JSX.Element|null}
 * @constructor
 */
export function FileGallery({
	files,
	onChange,
	readOnly,
	itemsPerRow = 4,
	onFileClickHandler,
	basic = false,
	extra,
	confirmAsOverlay,
}) {
	const [show, setShow] = useState(null);
	const [blobUrl, setBlobUrl] = useState(null);
	const closeModal = () => {
		setShow(null);
	};

	useEffect(() => {
		if (!show || !isAttachment(show)) return;
		let created = null;
		(async () => {
			try {
				const url = await fetchAsBlobUrl(API_service.API_BASE_URL + `file/doc/view/${show.bct}/${encodeURIComponent(show.img_fn)}`);
				created = url;
				setBlobUrl(url);
			} catch {}
		})();
		return () => {
			created?.startsWith("blob:") && URL.revokeObjectURL(created);
			setBlobUrl(null);
		};
	}, [show]);

	const [deleting, setDeleting] = useState(null);
	const deletePhoto = useAsyncUpdate(async file => {
		const promise = (async () => {
			if (confirmAsOverlay) {
				const deferred = defer();
				setDeleting({ ...deferred, file });
				return deferred.promise.finally(() => {
					setDeleting(null);
				});
			}
			return "yes" === (await app.confirm(`Delete ${file.comn || "Document"}`, `Are you sure?`));
		})();

		if (await promise) {
			return await onChange(
				files.filter(f => f !== file),
				[file],
			);
		}
	});

	if (!files) {
		return null;
	}

	const confirmationOverlay = file =>
		deleting?.file === file && (
			<div className="absolute inset-0 flex items-center justify-center">
				<div className="flex flex-col items-center justify-center space-y-2 rounded-md bg-white opacity-90 shadow-2xl p-2 md:px-4 md:py-6">
					<p className="mb-0 md:mb-auto">Are you sure?</p>
					<div className="space-y-1 md:space-y-0 md:space-x-4 flex flex-col md:flex-row">
						<Button type="button" secondary onClick={() => deleting.resolve(false)}>
							Cancel
						</Button>
						<Button type="button" color="red" onClick={() => deleting.resolve(true)}>
							Remove
						</Button>
					</div>
				</div>
			</div>
		);

	const confirmationOverlayBasic = file =>
		deleting?.file === file && (
			<div className="absolute bottom-14 -right-[24px] flex items-center justify-center w-[84px] md:!w-[108px] z-10">
				<div className="flex flex-col items-center justify-center space-y-2 rounded-md shadow-md bg-white p-2 md:px-2 md:py-3 w-full">
					<p className="mb-0 md:mb-auto">Remove?</p>
					<div className="gap-2 flex flex-col w-full">
						<Button type="button" fluid size="miniscule" basic onClick={() => deleting.resolve(true)}>
							Yes
						</Button>
						<Button type="button" fluid size="miniscule" basic onClick={() => deleting.resolve(false)}>
							No
						</Button>
					</div>
				</div>
				<Icon name="triangle down" className="absolute -bottom-1.5 text-white" />
			</div>
		);

	const preview = (file, basic) => (
		<div className={cls(deleting?.file === file && "blur-md")}>
			<FilePreview file={file} basic={basic} />
		</div>
	);

	if (basic) {
		return (
			<div className={`grid grid-cols-${itemsPerRow} gap-3`}>
				{files.map(file => (
					<div className="relative">
						<div className="overflow-hidden rounded-md" key={file.file_fn}>
							{preview(file, basic)}
						</div>
						{!readOnly && !file?.read_only && (
							<Button
								type="button"
								loading={!deleting && deletePhoto.loading}
								disabled={deleting || deletePhoto.loading}
								content={<SIcon size="xs" name="bin" bold />}
								basic
								className="absolute right-3 bottom-3 origin-bottom-right icon !bg-white !bg-opacity-50 hover:!bg-opacity-100 !rounded-sm !border-none transition-bg duration-200"
								data-testid="button-delete-file"
								style={{ height: "44px" }}
								onClick={() => deletePhoto.fn(file)}
							/>
						)}
						{confirmationOverlayBasic(file)}
					</div>
				))}
				{extra}
			</div>
		);
	}

	return (
		<>
			<CardGroup stackable doubling itemsPerRow={itemsPerRow}>
				{files.map(file => (
					<Card key={file.img_fn}>
						<Card.Content
							className="flex items-center justify-center cursor-pointer min-h-28 overflow-hidden"
							data-testid="image-preview"
							onClick={() => onFileClickHandler?.(file) || setShow(file)}
						>
							{preview(file)}
						</Card.Content>

						<Card.Content extra>
							<div className="flex-horizontal items-center justify-between">
								<div className="flex-vertical text-sm">
									<span>
										<SimpleShowMore content={file.comn} cut_length={50} />
									</span>
									<span>{formatDate(file.tm)}</span>
								</div>
								{!readOnly && !file?.read_only && (
									<Button
										type="button"
										loading={deletePhoto.loading}
										icon="trash alternate outline"
										basic
										className="self-end"
										data-testid="button-delete-file"
										onClick={() => deletePhoto.fn(file)}
									/>
								)}
								{confirmationOverlay(file)}
							</div>
						</Card.Content>
					</Card>
				))}
				{extra}
			</CardGroup>

			<Modal open={!!show} onClose={closeModal} closeIcon>
				<Modal.Header>{show?.comn}</Modal.Header>
				<Modal.Content>
					{isAttachment(show) ? (
						blobUrl ? (
							<iframe src={blobUrl} style={{ width: "100%", height: "70vh" }} />
						) : (
							<div className="p-8 text-center">Loading...</div>
						)
					) : (
						<FilePreview file={show} />
					)}
				</Modal.Content>
				<Modal.Actions>
					<Button basic content="Close" onClick={closeModal} />
				</Modal.Actions>
			</Modal>
		</>
	);
}

function FilePreview({ file, basic }) {
	if (isAttachment(file)) {
		return <Icon name="file pdf outline" size="huge" />;
	}

	return (
		<Image
			src={file.url}
			className={cls("mx-auto object-cover w-full rounded-md", basic && "h-[134px] lg:h-[160px]")}
		/>
	);
}

function isAttachment(file) {
	return file?.tag === "attachment" || file?.is_attachment;
}

function formatDate(file_tm) {
	const time = /^\d{12}$/.test(file_tm) ? moment(file_tm, "YYMMDDHHmmss") : moment(file_tm);

	if (!time.isValid()) {
		return null;
	}

	return time.format("D/M/YY");
}
